import { useEffect, useState } from 'react';
import { searchLimosaProjects, type LimosaProject } from '@/api/administratie';

/**
 * The projects a Limosa is linked to, as removable chips, with a search to add
 * one. Controlled: the caller decides whether a change is kept locally (upload
 * form) or saved at once (an existing record).
 */
export function LimosaProjects({ value, onChange, disabled }: {
  value: LimosaProject[];
  onChange: (next: LimosaProject[]) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LimosaProject[]>([]);
  const [searchError, setSearchError] = useState(false);
  const linked = new Set(value.map((p) => p.id));

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      searchLimosaProjects(q, ctrl.signal)
        .then((r) => { setResults(r); setSearchError(false); })
        .catch(() => { if (!ctrl.signal.aborted) setSearchError(true); });
    }, 300);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [query]);

  const add = (p: LimosaProject) => {
    onChange([...value, p]);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="text-xs text-td-muted" data-testid="limosa-projects">
      <p>Projecten</p>
      {value.length === 0
        ? <p className="mt-0.5 text-td-light">Geen project gekoppeld.</p>
        : (
          <ul className="mt-1 flex flex-wrap gap-1">
            {value.map((p) => (
              <li key={p.id} className="flex items-center gap-1 rounded-full bg-td-blue/10 pl-2.5 pr-1 py-0.5 text-td-navy">
                <span>{p.name}</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(value.filter((x) => x.id !== p.id))}
                  aria-label={`${p.name} loskoppelen`}
                  className="px-1 text-td-muted hover:text-red-600 disabled:opacity-60"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      <input
        type="search"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Project toevoegen: naam, S-nummer of klant"
        aria-label="Project toevoegen"
        className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:opacity-60"
      />
      {searchError && <p className="mt-0.5 text-red-600">Zoeken mislukt.</p>}
      {results.length > 0 && (
        <ul className="mt-1 border border-gray-200 rounded divide-y divide-gray-100 bg-white">
          {results.filter((r) => !linked.has(r.id)).map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => add(r)} className="w-full text-left px-2 py-1.5 text-sm text-td-navy hover:bg-gray-50">
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
